import { getSelectionBoxStyles } from "@/lib/reactflow-styles"
import { SelectionMode } from "@xyflow/react"
import type React from "react"
import type { CSSProperties } from "react"
import { useCallback, useRef, useState } from "react"

interface NodeSelectionBox {
	x: number
	y: number
	width: number
	height: number
	mode: "partial" | "full"
	selectedNodeIds?: string[]
}

interface UseNodeSelectionOptions {
	screenToFlowPosition: (position: { x: number; y: number }) => {
		x: number
		y: number
	}
	getSelectedNodeIds: () => string[]
	setSelecting: (value: boolean) => void
	updateSelectionBox: (selectionBox: NodeSelectionBox) => void
}

interface UseNodeSelectionResult {
	selectionMode: SelectionMode
	selectionBoxStyles: CSSProperties
	onSelectionStart: (event: React.MouseEvent) => void
	onSelectionEnd: (event: React.MouseEvent) => void
	onSelectionPointerMove: (event: React.MouseEvent) => void
}

function clearSelectionState(
	selectionStartX: React.MutableRefObject<number | null>,
	selectionStartFlow: React.MutableRefObject<{ x: number; y: number } | null>,
	setSelecting: (value: boolean) => void,
	setSelectionActive: (value: boolean) => void,
) {
	selectionStartX.current = null
	selectionStartFlow.current = null
	setSelecting(false)
	setSelectionActive(false)
}

export function useNodeSelection({
	screenToFlowPosition,
	getSelectedNodeIds,
	setSelecting,
	updateSelectionBox,
}: UseNodeSelectionOptions): UseNodeSelectionResult {
	const selectionStartX = useRef<number | null>(null)
	const selectionStartFlow = useRef<{ x: number; y: number } | null>(null)
	const [selectionMode, setSelectionMode] = useState<SelectionMode>(
		SelectionMode.Full,
	)
	const [isSelectionActive, setIsSelectionActive] = useState(false)

	const onSelectionStart = useCallback(
		(event: React.MouseEvent) => {
			selectionStartX.current = event.clientX
			selectionStartFlow.current = screenToFlowPosition({
				x: event.clientX,
				y: event.clientY,
			})
			setIsSelectionActive(true)
			setSelecting(true)
		},
		[screenToFlowPosition, setSelecting],
	)

	const onSelectionEnd = useCallback(
		(_event: React.MouseEvent) => {
			if (selectionStartX.current === null) {
				clearSelectionState(
					selectionStartX,
					selectionStartFlow,
					setSelecting,
					setIsSelectionActive,
				)
				return
			}

			clearSelectionState(
				selectionStartX,
				selectionStartFlow,
				setSelecting,
				setIsSelectionActive,
			)
		},
		[setSelecting],
	)

	const onSelectionPointerMove = useCallback(
		(event: React.MouseEvent) => {
			if (!selectionStartX.current || !selectionStartFlow.current) return

			const delta = event.clientX - selectionStartX.current
			const newMode = delta < 0 ? SelectionMode.Partial : SelectionMode.Full

			if (selectionMode !== newMode) {
				setSelectionMode(newMode)
			}

			const currentFlow = screenToFlowPosition({
				x: event.clientX,
				y: event.clientY,
			})

			const minX = Math.min(selectionStartFlow.current.x, currentFlow.x)
			const minY = Math.min(selectionStartFlow.current.y, currentFlow.y)
			const maxX = Math.max(selectionStartFlow.current.x, currentFlow.x)
			const maxY = Math.max(selectionStartFlow.current.y, currentFlow.y)

			updateSelectionBox({
				x: minX,
				y: minY,
				width: maxX - minX,
				height: maxY - minY,
				mode: delta < 0 ? "partial" : "full",
				selectedNodeIds: getSelectedNodeIds(),
			})
		},
		[
			screenToFlowPosition,
			selectionMode,
			updateSelectionBox,
			getSelectedNodeIds,
		],
	)

	const selectionBoxStyles = getSelectionBoxStyles(
		selectionMode,
		isSelectionActive,
	) as CSSProperties

	return {
		selectionMode,
		selectionBoxStyles,
		onSelectionStart,
		onSelectionEnd,
		onSelectionPointerMove,
	}
}
